/* ── Mobile media-query emulation for the Studio preview ────────────────────
 *
 * WHY THIS EXISTS (2026-08-21). The Studio's Mobile toggle renders the email in
 * a 380px DIV (`.paper.mobile`) inside a full-width browser window. Every ECH
 * block carries its own LOCKED mobile stacking CSS behind
 * `@media screen and (max-width:480px)` (Stensul guardrails: "the stacking CSS
 * is locked") — but a media query evaluates against the WINDOW, not the paper,
 * so none of those rules ever fire in the preview. The generic clamp
 * (`.paper.mobile table{max-width:100%}`) can't save the cases where a table's
 * min-content exceeds 380px — e.g. ECH_2CTA's two fixed 200px buttons + 30px
 * gutter = 460px — so the right side of the email clips (Jack's 2026-08-21
 * screenshot: logo and second CTA cut off).
 *
 * THE FIX BELONGS IN THE FRAME, NOT THE BLOCK (Jack's export-boundary rule,
 * monolith §2.3 comment): rewrite each block's mobile media-query rules into
 * rules scoped under `.es .paper.mobile`, so the block's OWN locked stacking
 * CSS fires exactly when the preview is in mobile mode — the preview then
 * reflows like a real ≤480px client without touching any block markup.
 *
 * The monolith (block mode) has the same gap; this is a frame-level preview
 * behavior, not a divergence in email OUTPUT — export HTML is unchanged.
 */

const SCOPE = ".es .paper.mobile";

/* A real 380px-wide client fires every `max-width` query ≥ its viewport width.
 * The paper is 380px, so emulate any query whose max-width covers 380px
 * (this includes the 400/460/480/499/500/599/600/640 zoo measured across the
 * production blocks). Queries that ALSO require min-width above 380 are
 * desktop-side and are skipped. */
const PAPER_W = 380;

function condFires(cond: string): boolean {
  const max = cond.match(/max-width:\s*(\d+(?:\.\d+)?)px/i);
  if (!max || parseFloat(max[1]) < PAPER_W) return false;
  const min = cond.match(/min-width:\s*(\d+(?:\.\d+)?)px/i);
  if (min && parseFloat(min[1]) > PAPER_W) return false;
  return true;
}

/* Prefix every selector of every rule in a media body with the scope.
 * `html`/`body` selectors can never match inside the paper — dropped. */
function scopeRules(body: string, scope: string, rootToScope = false): string {
  const rules: string[] = [];
  let i = 0;
  while (i < body.length) {
    const open = body.indexOf("{", i);
    if (open < 0) break;
    const selText = body.slice(i, open).trim();
    let depth = 1;
    let j = open + 1;
    while (j < body.length && depth > 0) {
      const c = body[j];
      if (c === "{") depth += 1;
      else if (c === "}") depth -= 1;
      j += 1;
    }
    const decls = body.slice(open + 1, j - 1).trim();
    i = j;
    if (!selText || !decls || selText.startsWith("@")) continue;
    const scoped = selText
      .split(",")
      .map((s) => s.trim())
      .map((s) => (rootToScope ? s.replace(/^(?::root|html)(?:\s+body)?\b|^body\b/i, "&") : s))
      .filter((s) => s && (rootToScope || !/^(html|body)\b/i.test(s)))
      .map((s) => (s === "&" ? scope : s.startsWith("&") ? `${scope}${s.slice(1)}` : `${scope} ${s}`))
      .join(",");
    if (scoped) rules.push(`${scoped}{${decls}}`);
  }
  return rules.join("\n");
}

/* ── Fixed-width tables (measured live in Jack's tab, 2026-08-21) ───────────
 * ECH_Logo's header table is `width="600" style="width:600px"` with NO
 * responsive class and NO media query of its own — and Chrome treats a table's
 * specified width as a FLOOR, so the paper clamp's `max-width:100%` computes
 * (`getComputedStyle` says 100%) yet the table still renders 600px and clips
 * (logo at x210 in a 380 paper, 10px cut). A DOM-marking pass was tried and
 * rejected: React 19 re-applies dangerouslySetInnerHTML on object identity, so
 * classes added to rendered blocks are wiped by the next render. Instead the
 * fluid rules are generated STATICALLY from the same block markup the media
 * scan reads — attribute selectors survive every re-render. */
function fluidTableCss(html: string, scope: string): string {
  const attrVals = new Set<string>();
  const styleVals = new Set<string>();
  for (const tag of html.match(/<table\b[^>]*>/gi) || []) {
    const attr = tag.match(/\bwidth\s*=\s*"?(\d+)"?/i);
    if (attr && parseInt(attr[1], 10) > PAPER_W) attrVals.add(attr[1]);
    const st = tag.match(/style\s*=\s*"[^"]*?(width:\s*(\d+(?:\.\d+)?)px)/i);
    if (st && parseFloat(st[2]) > PAPER_W) styleVals.add(st[1]);
  }
  const sels: string[] = [];
  attrVals.forEach((v) => sels.push(`${scope} table[width="${v}"]`));
  styleVals.forEach((v) => sels.push(`${scope} table[style*="${v}"]`));
  return sels.length ? `${sels.join(",")}{width:100% !important;max-width:100% !important}` : "";
}

/* Accepts either raw CSS text or HTML containing <style> tags (block markup is
 * passed straight in). Returns the scoped emulation stylesheet — empty string
 * when nothing in the source has a firing mobile query. */
export function mobileEmulationCss(source: string, scope: string = SCOPE): string {
  const css = /<style[\s>]/i.test(source)
    ? Array.from(source.matchAll(/<style\b[^>]*>([\s\S]*?)<\/style>/gi))
        .map((m) => m[1])
        .join("\n")
    : source;
  const out: string[] = [];
  let i = 0;
  for (;;) {
    const at = css.indexOf("@media", i);
    if (at < 0) break;
    const open = css.indexOf("{", at);
    if (open < 0) break;
    const cond = css.slice(at + 6, open);
    let depth = 1;
    let j = open + 1;
    while (j < css.length && depth > 0) {
      const c = css[j];
      if (c === "{") depth += 1;
      else if (c === "}") depth -= 1;
      j += 1;
    }
    const body = css.slice(open + 1, j - 1);
    i = j;
    if (!condFires(cond)) continue;
    const scoped = scopeRules(body, scope);
    if (scoped) out.push(scoped);
  }
  const fluid = fluidTableCss(source, scope);
  if (fluid) out.push(fluid);
  return out.join("\n");
}

/* ── A template's OWN shell, on the Studio canvas (2026-10-06) ────────────────
 * The canvas draws blocks straight into the page — no shell — so CSS that lives
 * only in a shell's <head> never reached it. With the default shell that was
 * harmless (its head mirrors the app's own canvas CSS). Justin's shell
 * (`ECH_V2_Shell`) carries the styles his modules are built against, so a canvas
 * without them would show a different email from the preview and the send
 * (Layne, Teams 10-06: "will the preview and the sent email look different?").
 * Fix in the frame again, never the export: the shell's head <style> rules are
 * rescoped under the paper; `html`/`body`/`:root` map to the paper itself.
 * Styles inside conditional comments (MSO-only) are skipped — they never apply
 * in a browser. `@media`/`@font-face` blocks are skipped here; the mobile half
 * goes through mobileEmulationCss like every block's own CSS. */
export function shellHeadStyles(shell: string): string {
  const s = String(shell || "");
  const end = s.search(/<\/head>/i);
  let head = end > -1 ? s.slice(0, end) : s;
  let prev: string;
  do {
    prev = head;
    head = head.replace(/<!--[\s\S]*?-->/g, "");
  } while (head !== prev);
  return Array.from(head.matchAll(/<style\b[^>]*>([\s\S]*?)<\/style>/gi)).map((m) => m[1]).join("\n");
}
export function shellCanvasCss(shell: string, mobile = false, scope: string = ".es .paper"): string {
  const css = shellHeadStyles(shell);
  if (!css.trim()) return "";
  const out = [scopeRules(css, scope, true)];
  if (mobile) out.push(mobileEmulationCss(css, `${scope}.mobile`));
  return out.filter(Boolean).join("\n");
}
